/**
 * Default avatars — simple abstract silhouettes for users without a photo.
 *
 * Usage:
 *   <Avatar src={user.profile_picture} name={user.name} gender={user.gender} size={80} />
 *
 * If `src` is set, renders the image. Otherwise falls back to a gender-
 * appropriate silhouette. If gender is unknown, uses a neutral silhouette.
 */
import { useState } from "react";

export default function Avatar({ src, name, gender, size = 64, className = "", testId }) {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;

  if (showImg) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        data-testid={testId}
        onError={() => setFailed(true)}
        className={`rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  const Silhouette = gender === "female" ? FemaleAvatar : gender === "male" ? MaleAvatar : NeutralAvatar;
  return (
    <div
      data-testid={testId}
      className={`rounded-full grid place-items-center shrink-0 ${className}`}
      style={{
        width: size, height: size,
        background: "linear-gradient(135deg, #eef2ff 0%, #fce7f3 100%)",
        color: "#4f46e5",
      }}
      aria-label={name || "avatar"}
    >
      <Silhouette size={size * 0.75} />
    </div>
  );
}

function MaleAvatar({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="22" r="10" fill="currentColor" opacity="0.7" />
      <path d="M12 56c0-11 9-18 20-18s20 7 20 18v4H12v-4z" fill="currentColor" opacity="0.7" />
      <path d="M22 15 Q 32 6, 42 15 Q 42 12, 32 10 Q 22 12, 22 15Z" fill="currentColor" opacity="0.9" />
    </svg>
  );
}

function FemaleAvatar({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="22" r="10" fill="currentColor" opacity="0.7" />
      <path d="M12 56c0-11 9-18 20-18s20 7 20 18v4H12v-4z" fill="currentColor" opacity="0.7" />
      <path d="M20 20 Q 22 8, 32 8 Q 42 8, 44 20 Q 44 24, 40 22 Q 38 15, 32 15 Q 26 15, 24 22 Q 20 24, 20 20Z" fill="currentColor" opacity="0.95" />
    </svg>
  );
}

function NeutralAvatar({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="22" r="10" fill="currentColor" opacity="0.7" />
      <path d="M12 56c0-11 9-18 20-18s20 7 20 18v4H12v-4z" fill="currentColor" opacity="0.7" />
    </svg>
  );
}
